// core
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

// ui primitives (replaced @mui/material — see Components/UI)
import {
    Card,
    CircularProgress,
    GoogleIcon,
    Stack,
    Typography,
} from "Components/UI";
import { GoogleLogin } from "@react-oauth/google";

// services
import { consumerGoogleLogin } from "Services/LoginAPI";

// components
import LoginLogo from "./LoginLogo";

// styles
import "react-toastify/dist/ReactToastify.css";
import classNames from "classnames";
import styles from "./LoginForm.module.css";
import socialStyles from "./SocialLogins.module.css";

/**
 * Submits a SAML assertion via a hidden auto-submitting form.
 * This is the standard mechanism for SAML SP-initiated flows.
 */
function submitSamlForm(samlUrl, samlResponse, relayState) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = samlUrl;
    // A class, not form.style.display: assigning to .style writes an inline style
    // attribute, which style-src 'self' blocks.
    form.className = styles.hiddenForm;

    const samlInput = document.createElement("input");
    samlInput.type = "hidden";
    samlInput.name = "SAMLResponse";
    samlInput.value = samlResponse;
    form.appendChild(samlInput);

    if (relayState) {
        const relayInput = document.createElement("input");
        relayInput.type = "hidden";
        relayInput.name = "RelayState";
        relayInput.value = relayState;
        form.appendChild(relayInput);
    }

    document.body.appendChild(form);
    form.submit();
}

export default function ConsumerLoginForm() {
    // own state
    const [loading, setLoading] = useState(false);

    const handleGoogleSuccess = useCallback(
        async (credentialResponse) => {
            setLoading(true);

            try {
                const response = await consumerGoogleLogin(credentialResponse);

                if (!response) {
                    toast.error("Communication error occurred.");
                    setLoading(false);
                    return;
                }

                if (!response.status) {
                    toast.error(
                        response.info ||
                            response.message ||
                            "Authentication failed.",
                    );
                    setLoading(false);
                    return;
                }

                // Handle SAML assertion POST flow
                if (response.saml_url && response.saml_response) {
                    submitSamlForm(
                        response.saml_url,
                        response.saml_response,
                        response.relay_state,
                    );
                    return;
                }

                // Handle simple redirect flow (fallback)
                if (response.redirect_url) {
                    window.location.href = response.redirect_url;
                    return;
                }

                toast.error("Unexpected response from server.");
                setLoading(false);
            } catch (error) {
                toast.error(error?.message || error?.toString());
                console.error("Consumer login error:", error);
                setLoading(false);
            }
        },
        [],
    );

    // render
    return (
        <Card flat>
            <Stack fullWidth gap={3}>
                {/* Logo */}
                <Stack align="center" direction="row" justify="center">
                    <LoginLogo />
                </Stack>

                {/* Heading -- distinct from admin "Please Sign In" */}
                <Stack align="center" justify="center" gap={0.5}>
                    <Typography variant="h6">User Sign In</Typography>
                    <Typography align="center" color="secondary" variant="body2">
                        Sign in with your Google account to continue
                    </Typography>
                </Stack>

                {/* Google Sign In Button */}
                {loading ? (
                    <Stack
                        align="center"
                        className={styles.authenticatingBlock}
                        justify="center"
                    >
                        <CircularProgress color="primary" size={32} />
                        <Typography
                            className={styles.authenticatingLabel}
                            color="secondary"
                            variant="body2"
                        >
                            Authenticating...
                        </Typography>
                    </Stack>
                ) : (
                    <div
                        className={classNames(
                            socialStyles.button,
                            socialStyles.outlined,
                            socialStyles.buttonTall,
                            socialStyles.block,
                        )}
                    >
                        <span className={socialStyles.inner}>
                            <span className={socialStyles.iconBox}>
                                <GoogleIcon className={socialStyles.icon} />
                            </span>
                            <span className={socialStyles.label}>
                                Sign in with Google
                            </span>
                        </span>

                        <GoogleLogin
                            text="signin_with"
                            onSuccess={(credentialResponse) => {
                                handleGoogleSuccess(credentialResponse);
                            }}
                            onError={() => {
                                toast.error("Google sign-in failed.");
                                setLoading(false);
                            }}
                            containerProps={{
                                className: socialStyles.googleOverlay,
                            }}
                        />
                    </div>
                )}

                {/* Back to admin login link */}
                <Stack align="center" justify="center">
                    <Link
                        to="/"
                        className={classNames(styles.link, styles.linkThemed)}
                    >
                        <Typography variant="body2">
                            Admin? Sign in here
                        </Typography>
                    </Link>
                </Stack>
            </Stack>
        </Card>
    );
}
